/**
 * @fileoverview 서비스 간 검색 시 URL 검색 파라미터를 관리하는 유틸리티
 *
 * 필수 파라미터:
 * - region (지역): 지역 ID와 이름으로 구성된 정보
 * - search (검색어): 검색 키워드
 *
 * 기능:
 * - 서로 다른 서비스로 전환할 때: 필수 파라미터(region, search)만 유지하고 다른 모든 파라미터 초기화
 * - 같은 서비스 내에서 검색할 때는 서비스별로 다른 정책 적용:
 *   - 필터 초기화 서비스: 중고거래, 자동차, 로컬프로필, 모임
 *   - 필터 유지 서비스: 부동산, 알바, 동네생활
 *
 * 참고:
 * - 필수 파라미터는 항상 기존 값보다 우선 적용됨
 * - 필터 유지 서비스에서는 in, search 외의 모든 필터 값이 유지됨
 */

import {
  CommonSearchParamKey,
  buildRegionParamValue,
} from "@karrotmarket-com/core";
import { Services } from "~/core/services";
import type { ServicesOption } from "./SearchForm";

export function getSearchParamsForService({
  requiredValues,
  current,
  next,
}: {
  requiredValues: {
    region: {
      id: string;
      name?: string;
    };
    search: string;
  };
  current: {
    service: ServicesOption;
    searchParams: URLSearchParams;
  };
  next: {
    service: ServicesOption;
  };
}): URLSearchParams {
  const requiredSearchParams = new URLSearchParams([
    [
      CommonSearchParamKey.In,
      buildRegionParamValue({
        region: requiredValues.region,
      }),
    ],
    [CommonSearchParamKey.Search, requiredValues.search],
  ]);
  if (current.service !== next.service) {
    return requiredSearchParams;
  }

  return servicesNextSearchParamsMap[current.service]({
    requiredSearchParams,
    currentSearchParams: current.searchParams,
  });
}

type ServiceSearchParamsHandler = (params: {
  requiredSearchParams: URLSearchParams;
  currentSearchParams: URLSearchParams;
}) => URLSearchParams;

const keepServiceFilters: ServiceSearchParamsHandler = ({
  requiredSearchParams,
  currentSearchParams,
}) => {
  const filteredCurrentSearchParams = new URLSearchParams(currentSearchParams);
  for (const commonKey of requiredSearchParams.keys()) {
    filteredCurrentSearchParams.delete(commonKey);
  }

  return new URLSearchParams([
    ...requiredSearchParams.entries(),
    ...filteredCurrentSearchParams.entries(),
  ]);
};

const resetServiceFilters: ServiceSearchParamsHandler = ({
  requiredSearchParams,
}) => new URLSearchParams(requiredSearchParams);

const servicesNextSearchParamsMap: Record<
  ServicesOption,
  ServiceSearchParamsHandler
> = {
  [Services.BUY_SELL]: resetServiceFilters,
  [Services.CARS]: resetServiceFilters,
  [Services.LOCAL_PROFILE]: resetServiceFilters,
  [Services.GROUP]: resetServiceFilters,

  [Services.REALTY]: keepServiceFilters,
  [Services.JOBS]: keepServiceFilters,
  [Services.COMMUNITY]: keepServiceFilters,
};
